import React, { useState } from "react";
import { Form, Row, Col } from "react-bootstrap";
import { updateDb } from "../services/updateDb";

import { useNavigate } from "react-router-dom";
import { useAppContext } from "../AppContext";
import AlertMessage from "../common/AlertMessage";
import moment from "moment";
import { useTranslation } from 'react-i18next';

const EnvironmentForm = ({ environmentData }) => {
    const { alertMessage, showAlertMessage } = useAppContext();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [enableOverrides, setEnableOverrides] = useState(false);

    const submitForm = async (e) => {
        e.preventDefault();
    
        const formData = new FormData(e.target);

        // Handle trial expiration
        let trialExpiresValue;
        const trialType = formData.get('trialType');
        if (trialType === 'custom') {
            const customDate = formData.get('customTrialDate');
            trialExpiresValue = customDate ? new Date(customDate).getTime() : 0;
        } else {
            trialExpiresValue = parseInt(formData.get('trialEnvironment'));
        }

        // Build subscription plan (all plans are yearly now)
        const subscriptionPlan = {
            planId: formData.get('planId') || null,
            customPrice: formData.get('customPrice') ? parseFloat(formData.get('customPrice')) : null,
            featureOverrides: enableOverrides ? {
                maxOrganizations: formData.get('maxOrganizations') ? parseInt(formData.get('maxOrganizations')) : undefined,
                maxProjects: formData.get('maxProjects') ? parseInt(formData.get('maxProjects')) : undefined,
                maxUsers: formData.get('maxUsers') ? parseInt(formData.get('maxUsers')) : undefined,
                aiCallsPerWeek: formData.get('aiCallsPerWeek') ? parseInt(formData.get('aiCallsPerWeek')) : undefined
            } : {}
        };

        let envObj = {
            name: formData.get('name'),
            userName: formData.get('userName'),
            role: formData.get('role'),
            email: formData.get('email'),
            password: formData.get('password'),
            orgName: formData.get('orgName'),
            projectName: formData.get('projectName'),
            importRiskTemplates: formData.get('importRiskTemplates') === "on" ? true : false,
            trialExpires: trialExpiresValue,
            subscriptionPlan: subscriptionPlan.planId ? subscriptionPlan : undefined,
            subscriptionExpires: formData.get('subscriptionExpires') 
                ? new Date(formData.get('subscriptionExpires')).getTime()
                : 0,
            parentEnvironment: 'main'
        };
        
        const res = await updateDb("/api/environments/", envObj);

        if (res.success) {
            navigate("/environments", { state: { message: t('environment_added'), type: "success" } });
        } 
        else {
            showAlertMessage('error', res.error ? res.error : t('error_adding_environment'));   
        }
    };

    
    return (
    <Form onSubmit={submitForm} id="addEnvironment">

        <AlertMessage type={alertMessage.type} message={alertMessage.content} />
        
        <Row className="mb-3">

            <Form.Group as={Col} >
                <Form.Label><span className="blue-bg">{t('environment')} {t('name')}</span></Form.Label>
                <Form.Control
                    as="input"
                    name="name"
                    defaultValue={environmentData ? environmentData.name : ""}
                    placeholder={t('environment_name_placeholder')}
                    required
                    onChange={e => {
                        const sanitizedValue = e.target.value.toLowerCase().replace(/\s+/g, '');
                        e.target.value = sanitizedValue;
                    }}
                />
            </Form.Group>
        </Row>

        <Row className="mb-3">

            <Form.Group as={Col} >
                <Form.Label><span className="blue-bg">{t('user')} {t('name')}</span></Form.Label>
                <Form.Control
                    as="input"
                    name="userName"
                    defaultValue={environmentData ? environmentData.userName : ""}
                    placeholder={t('superadmin_user_name')}
                    required
                />
            </Form.Group>
        </Row>

        <Row className="mb-3">

            <Form.Group as={Col} >
                <Form.Label><span className="blue-bg">{t('user_role')}</span></Form.Label>
                <Form.Select name="role" defaultValue={environmentData ? environmentData.role : "superadmin"} required>
                    <option value="superadmin">Superadmin</option>
                    <option value="admin">Admin</option>
                    <option value="user">{t('user')}</option>
                </Form.Select>
            </Form.Group>
        </Row>


        <Row className="mb-3">

            <Form.Group as={Col} >
                <Form.Label><span className="blue-bg">{t('user')} Email</span></Form.Label>
                <Form.Control
                    as="input"
                    type="email"
                    name="email"
                    defaultValue={environmentData ? environmentData.email : ""}
                    placeholder={t('superadmin_user_email')}
                    required
                />
            </Form.Group>
        </Row>

        <Row className="mb-3">

            <Form.Group as={Col} >
                <Form.Label><span className="blue-bg">{t('user')}  {t('password')}</span></Form.Label>
                <Form.Control
                    as="input"
                    type="password"
                    name="password"
                    placeholder={t('superadmin_user_password')}
                    required
                />
            </Form.Group>
        </Row>

        <Row className="mb-3">

            <Form.Group as={Col} >
                <Form.Label><span className="blue-bg">{t('organization')}</span></Form.Label>
                <Form.Control
                    as="input"
                    name="orgName"
                    defaultValue={environmentData ? environmentData.orgName : ""}
                    placeholder={t('initial_organization')}
                    required
                />
            </Form.Group>
        </Row>

        <Row className="mb-3">

            <Form.Group as={Col} >
                <Form.Label><span className="blue-bg">{t('domain')}</span></Form.Label>
                <Form.Control
                    as="input"
                    name="projectName"
                    defaultValue={environmentData ? environmentData.projectName : ""}
                    placeholder={t('initial_domain_name')}
                    required
                />
            </Form.Group>
        </Row>

        <Row className="mb-3">
            <Form.Group as={Col} >

                <Form.Label><span className="blue-bg">Import {t('risk_templates')}</span></Form.Label>

                <Form.Check
                    type="checkbox"
                    id="importRiskTemplates"
                    name="importRiskTemplates"
                    label="Import Risk Templates"
                    defaultChecked="true"
                />
            </Form.Group>
        </Row>


        <div className="vertical-spacer" />
        <h4>{t('subscription_configuration')}</h4>

        <Row className="mb-3">
            <Form.Group as={Col}>
                <Form.Label><span className="blue-bg">{t('plan')} *</span></Form.Label>
                <Form.Select
                    name="planId"
                    defaultValue={environmentData?.subscriptionPlan?.planId || ""}
                    required
                >
                    <option value="">{t('select_plan')}</option>
                    <option value="trial">Trial</option>
                    <option value="free">Free</option>
                    <option value="starter">Starter</option>
                    <option value="premium">Premium</option>
                    <option value="knmt-starter">KNMT Starter</option>
                </Form.Select>
            </Form.Group>
        </Row>

        <Row className="mb-3">
            <Form.Group as={Col}>
                <Form.Label><span className="blue-bg">{t('trial_expiration')}</span></Form.Label>
                <Form.Select
                    name="trialType"
                    defaultValue="preset"
                    onChange={(e) => {
                        document.getElementById('presetTrialOptions').style.display = e.target.value === 'preset' ? 'block' : 'none';
                        document.getElementById('customTrialDate').style.display = e.target.value === 'custom' ? 'block' : 'none';
                    }}
                >
                    <option value="preset">{t('preset_duration')}</option>
                    <option value="custom">{t('custom_date')}</option>
                </Form.Select>
            </Form.Group>
        </Row>

        <Row className="mb-3" id="presetTrialOptions">
            <Form.Group as={Col}>
                <Form.Select
                    name="trialEnvironment"
                    defaultValue={environmentData?.trialExpires || "0"}
                >
                    <option value="0">{t('no_trial_permanent')}</option>
                    <option value="604800">{t('trial_7_days')}</option>
                    <option value="1209600">{t('trial_14_days')}</option>
                    <option value="2592000">{t('trial_30_days')}</option>
                    <option value="5184000">{t('trial_60_days')}</option>
                </Form.Select>
            </Form.Group>
        </Row>

        <Row className="mb-3" id="customTrialDate" style={{ display: 'none' }}>
            <Form.Group as={Col}>
                <Form.Control
                    type="date"
                    name="customTrialDate"
                    placeholder={t('select_trial_expiration_date')}
                />
            </Form.Group>
        </Row>

        <Row className="mb-3">
            <Form.Group as={Col}>
                <Form.Label><span className="blue-bg">{t('subscription_expiration_date')}</span></Form.Label>
                <Form.Control
                    type="date"
                    name="subscriptionExpires"
                    defaultValue={environmentData?.subscriptionExpires 
                        ? moment(environmentData.subscriptionExpires).format('YYYY-MM-DD')
                        : ''}
                />
                <Form.Text className="text-muted">
                    {t('optional_leave_empty_no_expiration')}
                </Form.Text>
            </Form.Group>
        </Row>

        <Row className="mb-3">
            <Form.Group as={Col}>
                <Form.Label><span className="blue-bg">{t('custom_price_eur')}</span></Form.Label>
                <Form.Control
                    type="number"
                    step="0.01"
                    name="customPrice"
                    defaultValue={environmentData?.subscriptionPlan?.customPrice || ''}
                    placeholder={t('leave_empty_default_price')}
                />
                <Form.Text className="text-muted">
                    {t('override_default_plan_price')}
                </Form.Text>
            </Form.Group>
        </Row>

        <div className="vertical-spacer" />
        <h4>{t('feature_limits')}</h4>

        <Row className="mb-3">
            <Form.Group as={Col}>
                <Form.Check
                    type="checkbox"
                    id="enableOverrides"
                    name="enableOverrides"
                    label={t('enable_custom_feature_overrides')}
                    checked={enableOverrides}
                    onChange={(e) => setEnableOverrides(e.target.checked)}
                />
                <Form.Text className="text-muted">
                    {t('override_default_plan_features')}
                </Form.Text>
            </Form.Group>
        </Row>

        <div style={{ opacity: enableOverrides ? 1 : 0.5, pointerEvents: enableOverrides ? 'auto' : 'none' }}>
            <Row className="mb-3">
                <Form.Group as={Col}>
                    <Form.Label><span className="blue-bg">{t('max_organizations')}</span></Form.Label>
                    <Form.Control
                        type="number"
                        name="maxOrganizations"
                        defaultValue={environmentData?.subscriptionPlan?.featureOverrides?.maxOrganizations || ''}
                        placeholder={t('leave_empty_default_unlimited')}
                        disabled={!enableOverrides}
                    />
                </Form.Group>
            </Row>

            <Row className="mb-3">
                <Form.Group as={Col}>
                    <Form.Label><span className="blue-bg">{t('max_domains')}</span></Form.Label>
                    <Form.Control
                        type="number"
                        name="maxProjects"
                        defaultValue={environmentData?.subscriptionPlan?.featureOverrides?.maxProjects || ''}
                        placeholder={t('leave_empty_default_unlimited')}
                        disabled={!enableOverrides}
                    />
                </Form.Group>
            </Row>

            <Row className="mb-3">
                <Form.Group as={Col}>
                    <Form.Label><span className="blue-bg">{t('max_users')}</span></Form.Label>
                    <Form.Control
                        type="number"
                        name="maxUsers"
                        defaultValue={environmentData?.subscriptionPlan?.featureOverrides?.maxUsers || ''}
                        placeholder={t('leave_empty_default_unlimited')}
                        disabled={!enableOverrides}
                    />
                </Form.Group>
            </Row>

            <Row className="mb-3">
                <Form.Group as={Col}>
                    <Form.Label><span className="blue-bg">{t('ai_calls_per_week')}</span></Form.Label>
                    <Form.Control
                        type="number"
                        name="aiCallsPerWeek"
                        defaultValue={environmentData?.subscriptionPlan?.featureOverrides?.aiCallsPerWeek || ''}
                        placeholder={t('leave_empty_default_unlimited')}
                        disabled={!enableOverrides}
                    />
                </Form.Group>
            </Row>
        </div>

        <div className="vertical-spacer" />

        <input className="white-border-btn submit-btn" type="submit" value={environmentData ? t('update_environment') : t('add_environment')} />
            
    </Form>
);
};
export default EnvironmentForm;
